import { useMutation, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useCommonApi } from '../../api';
import { onSuccessCustomApiResponse } from '../../api/utils';
import { useNotify } from '../../hooks';
import { transformFilesToBase64 } from '../../utils/transformFilesToBase64Format';
import { parseFilesToUploadRequest } from '../accidents/parser';
import { Accident } from '../types/accident';

export const useUploadFiles = (accident: Accident) => {
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState<
    | {
        title?: string;
        name: string;
        limit: number;
        accidentId: string;
      }
    | undefined
  >();

  const { notify } = useNotify();

  const [imagesByCategory, setImagesByCategory] = useState<{ category: string; images: File[] }[]>([]);
  const queryClient = useQueryClient();

  const { mutate: uploadFiles, isPending: isUploadingFiles } = useMutation(useCommonApi().uploadAccidentFiles());

  const handleFileOptimization = async (file: File) => {
    if (!file.type.startsWith('image/')) return file;
    const options = {
      maxSizeMB: 1,
      alwaysKeepResolution: true,
      maxWidthOrHeight: 2000
    };
    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error('Error optimizing file:', error);
      return file;
    }
  };

  const handleSetImage = (category: string, images: File[], autoUpload?: boolean) => {
    setImagesByCategory((prev) => {
      const existingCategoryIndex = prev.findIndex((item) => item.category === category);

      let updatedCategories: any;
      if (existingCategoryIndex !== -1) {
        updatedCategories = [...prev];
        updatedCategories[existingCategoryIndex] = { category, images };
      } else {
        updatedCategories = [...prev, { category, images }];
      }

      if (autoUpload) {
        handleUploadFiles(images, category);
      }

      return updatedCategories;
    });
  };

  const handleUploadImages = async (callback?: () => void) => {
    if (isEmpty(imagesByCategory)) return;
    setIsUploadingImages(true);
    try {
      const optimizedImagesByCategory = await Promise.all(
        imagesByCategory.map(async (imageCategory) => {
          const optimizedImages = await Promise.all(imageCategory.images.map(handleFileOptimization));
          return { ...imageCategory, images: optimizedImages };
        })
      );

      const uploadPromises = optimizedImagesByCategory.map(async (imageCategory) => {
        const { files: transferFiles } = await parseFilesToUploadRequest(imageCategory.images, imageCategory.category);

        return uploadFiles({
          filesData: {
            accidentId: accident?.id as number,
            files: transferFiles
          }
        });
      });

      await Promise.all(uploadPromises);

      await queryClient.invalidateQueries({
        queryKey: ['common', 'accidents', 'detail', accident?.id.toString()]
      });

      notify('success', 'Soubory byly úspěšně nahrány!');
      callback?.();
      setImagesByCategory([]);
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleUploadFiles = async (files: File[], name: string) => {
    setIsUploadingImages(true);
    const optimizedFiles = await Promise.all(files.map(handleFileOptimization));
    const base64Files = await transformFilesToBase64(optimizedFiles);
    const filesToTransfer = base64Files.map((file) => ({
      filename: file.name,
      data: file.data as string,
      template: name
    }));

    uploadFiles(
      {
        filesData: {
          accidentId: accident.id,
          files: filesToTransfer
        }
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            queryClient.invalidateQueries({
              queryKey: ['common', 'accidents', 'detail', accident.id.toString()]
            });
            setOpenUploadModal(undefined);
          }
        }),
        onSettled: () => setIsUploadingImages(false)
      }
    );
  };

  return {
    isUploadingImages,
    handleUploadImages,
    handleUploadFiles,
    handleSetImage,
    openUploadModal,
    setOpenUploadModal,
    isUploadingFiles
  };
};
